/**
 * The retained vector model — the single source of truth for the whole app.
 *
 * Every stroke is real path geometry (never pixels), so SVG/PDF export is lossless.
 * `PathData` maps 1:1 to an SVG `<path d>`, a Canvas2D Path2D, and a PDF content stream.
 */

export interface Point {
  x: number;
  y: number;
}

/** Colour channels 0..255, alpha 0..1. */
export interface RGBA {
  r: number;
  g: number;
  b: number;
  a: number;
}

export type PathSegment =
  | { t: 'M'; p: Point }
  | { t: 'L'; p: Point }
  | { t: 'Q'; c: Point; p: Point }
  | { t: 'C'; c1: Point; c2: Point; p: Point }
  | { t: 'Z' };

export interface PathData {
  segments: PathSegment[];
}

export type ShapeMode = 'stroke' | 'fill';

export interface ShapeStyle {
  mode: ShapeMode;
  color: RGBA;
  lineWidth: number;
  join: CanvasLineJoin;
  cap: CanvasLineCap;
  /** Fill winding rule (default 'nonzero'). Preserved so imported glyphs keep their holes. */
  fillRule?: CanvasFillRule;
}

/** Centreline + per-point width — flattened to a fill outline at commit. */
export interface VariableWidth {
  spine: Point[];
  width: number[];
}

export interface GradientStop {
  offset: number;
  color: RGBA;
}

export interface GradientSpec {
  from: Point;
  to: Point;
  stops: GradientStop[];
}

export interface ShapeMeta {
  module: string;
  seq: number;
  t: number;
}

export interface Shape {
  id: string;
  path: PathData;
  style: ShapeStyle;
  variableWidth?: VariableWidth;
  gradient?: GradientSpec;
  meta: ShapeMeta;
}

export interface Scene {
  width: number;
  height: number;
  background: RGBA;
  /** Session swatch list. */
  palette: RGBA[];
  /** Index into `palette` of the active ink. */
  activeInk: number;
  shapes: Shape[];
}

export const BLACK: RGBA = { r: 17, g: 17, b: 17, a: 1 };
export const WHITE: RGBA = { r: 255, g: 255, b: 255, a: 1 };

export function rgba(r: number, g: number, b: number, a = 1): RGBA {
  return { r, g, b, a };
}

/** Per-channel (and alpha) linear interpolation, for palette ramps. */
export function lerpRgba(a: RGBA, b: RGBA, t: number): RGBA {
  const u = 1 - t;
  return { r: a.r * u + b.r * t, g: a.g * u + b.g * t, b: a.b * u + b.b * t, a: a.a * u + b.a * t };
}

export function cssColor(c: RGBA): string {
  return `rgba(${c.r | 0}, ${c.g | 0}, ${c.b | 0}, ${c.a})`;
}

export function defaultStyle(): ShapeStyle {
  return { mode: 'stroke', color: { ...BLACK }, lineWidth: 2, join: 'round', cap: 'round' };
}

export function makeScene(width: number, height: number): Scene {
  return {
    width,
    height,
    background: { ...WHITE },
    palette: [{ ...BLACK }, rgba(214, 40, 120), rgba(0, 180, 200), rgba(240, 170, 40), { ...WHITE }],
    activeInk: 0,
    shapes: [],
  };
}

export function parseColor(hex: string): RGBA {
  let h = hex.trim();
  if (h.startsWith('#')) h = h.slice(1);
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return {
    r: Number.isNaN(r) ? 0 : r,
    g: Number.isNaN(g) ? 0 : g,
    b: Number.isNaN(b) ? 0 : b,
    a: 1,
  };
}

export function colorToHex(c: RGBA): string {
  const h = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${h(c.r)}${h(c.g)}${h(c.b)}`;
}

export function hslToRgba(h: number, s: number, l: number, a = 1): RGBA {
  const hue = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (hue < 60) {
    r = c;
    g = x;
  } else if (hue < 120) {
    r = x;
    g = c;
  } else if (hue < 180) {
    g = c;
    b = x;
  } else if (hue < 240) {
    g = x;
    b = c;
  } else if (hue < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255), a };
}

function cloneSeg(s: PathSegment): PathSegment {
  switch (s.t) {
    case 'M':
      return { t: 'M', p: { ...s.p } };
    case 'L':
      return { t: 'L', p: { ...s.p } };
    case 'Q':
      return { t: 'Q', c: { ...s.c }, p: { ...s.p } };
    case 'C':
      return { t: 'C', c1: { ...s.c1 }, c2: { ...s.c2 }, p: { ...s.p } };
    case 'Z':
      return { t: 'Z' };
    default:
      throw new Error('unknown path segment');
  }
}

export function clonePath(p: PathData): PathData {
  return { segments: p.segments.map(cloneSeg) };
}

export function cloneStyle(s: ShapeStyle): ShapeStyle {
  return { ...s, color: { ...s.color } };
}

export function cloneShape(s: Shape): Shape {
  return {
    id: s.id,
    path: clonePath(s.path),
    style: cloneStyle(s.style),
    variableWidth: s.variableWidth
      ? { spine: s.variableWidth.spine.map((p) => ({ ...p })), width: [...s.variableWidth.width] }
      : undefined,
    gradient: s.gradient
      ? {
          from: { ...s.gradient.from },
          to: { ...s.gradient.to },
          stops: s.gradient.stops.map((st) => ({ offset: st.offset, color: { ...st.color } })),
        }
      : undefined,
    meta: { ...s.meta },
  };
}
